<template>
    <div class="list-item-alt">
        <div class="list-item-info-alt">
            <div class="category-header-left">
                <input type="checkbox" ref="categoryCheckbox" :checked="checkState === 'all'"
                    @change="clickCategoryCheckbox" :aria-label="`Activar todas las capas de ${category.name}`">
             <strong v-if="searchQuery" v-html="highlightMatch(category.name, searchQuery)"
                :class="{ 'search-match': category.isMatch }"></strong>
            <strong v-else class="category-name">{{ category.name }}</strong>
           </div>

            <div class="list-item-info-alt">
                <div class="option" style="padding-left: 0em;">
                    <button class="btn-collapsible" @click="toggleList" :aria-expanded="showList"
                        :aria-label="`${showList ? 'Colapsar' : 'Expandir'} ${category.name}`">
                        <font-awesome-icon v-if="!showList" icon="chevron-down"
                            style="font-size: 0.8em;"></font-awesome-icon>
                        <font-awesome-icon v-else icon="chevron-up" style="font-size: 1em;"></font-awesome-icon>
                    </button>
                </div>
            </div>
        </div>

        <transition name="expand">
            <div class="items-list-collap" v-show="showList">
                <div v-for="(layer, j) in category.layers" :key="j"
                    :class="['layer-item', { 'search-match-layer': isLayerMatch(layer) }]">

                    <div class="layer-item-row">
                        <div class="layer-item-content">
                            <input type="checkbox" :id="`layer-${index}-${j}`"
                                :checked="isExternal ? layer.visible : layer.selected"
                                @change="clickLayer(j, $event.target.checked)"
                                @focus="onCheckboxFocus(layer, $event)" @blur="hideInfoTip"
                                :aria-describedby="infoTip && infoTip.layerId === layer.id ? `layer-info-tip-${index}` : null"
                                :aria-label="`${isExternal ? 'Mostrar u ocultar' : 'Seleccionar'} capa ${layer.name}`">
                            <label :for="`layer-${index}-${j}`" class="layer-label"
                                @mouseenter="scheduleInfoTip(layer, $event.currentTarget)"
                                @mouseleave="hideInfoTip" @mousedown="hideInfoTip">
                                <span v-if="searchQuery" v-html="highlightMatch(layer.name, searchQuery)"></span>
                                <span v-else>{{ layer.name }}</span>
                            </label>
                        </div>

                        <div class="layer-actions">
                            <!-- Simbología: sólo si la capa tiene alguna guardada -->
                            <select v-if="!isExternal && symbologiesFor(layer).length"
                                class="symbology-select"
                                :value="selectedSymbology(layer)"
                                @change="$emit('selectSymbology', layer, $event.target.value)"
                                :title="`Simbología de ${layer.name}`"
                                :aria-label="`Simbología de ${layer.name}`" @click.stop>
                                <option value="">Predeterminada</option>
                                <option v-for="s in symbologiesFor(layer)" :key="s.id" :value="s.id">{{ s.name }}</option>
                            </select>

                            <!-- Control de Opacidad -->
                            <div class="opacity-control">
                                <div class="opacity-dot" @click="toggleOpacitySlider(j)"
                                    :title="`Opacidad: ${getLayerOpacity(layer)}%`"
                                    :class="{ 'active': activeOpacityLayer === j }"></div>

                                <!-- Slider que aparece al hacer click -->
                                <transition name="fade">
                                    <div v-if="activeOpacityLayer === j" class="opacity-slider-panel">
                                        <span class="opacity-label">Opacidad</span>
                                        <input type="range" min="0" max="1" step="0.01"
                                            :value="layer.opacity !== undefined ? layer.opacity : 1"
                                            @input="updateOpacity($event, j, layer)" class="opacity-slider" @click.stop />
                                        <span class="opacity-value">{{ getLayerOpacity(layer) }}%</span>
                                    </div>
                                </transition>
                            </div>

                            <font-awesome-layers v-if="!isExternal" class="action-icon filter-state-icon"
                                :class="filterState(layer)" :title="filterIconTitle(layer)"
                                @click.stop="toggleLayerFilter(layer)" :aria-label="filterIconTitle(layer)">
                                <font-awesome-icon icon="filter" />
                                <font-awesome-icon v-if="filterState(layer) === 'unconfigured'" icon="plus" class="filter-plus" />
                            </font-awesome-layers>

                            <!-- Filtros guardados: siempre visible (igual que el embudo), abre el
                                 modal de administración — se pinta activo si el filtro guardado
                                 aplicado ahora mismo coincide con un preset -->
                            <font-awesome-icon v-if="!isExternal" icon="bookmark"
                                class="action-icon saved-filter-icon" :class="{ active: !!selectedSavedFilterId(layer) }"
                                :title="selectedSavedFilterId(layer) ? 'Filtro guardado activo — Administrar filtros guardados' : 'Administrar filtros guardados'"
                                @click.stop="clickManageSavedFilters(layer)"
                                :aria-label="`Filtros guardados de ${layer.name}`" />

                            <!-- 🔍 NUEVO BOTÓN DE LUPA -->

                            <font-awesome-layers @click="setLayerMenu(j)" class="fa-lg layer-menu-icon action-icon"
                                :aria-label="`Menú de opciones para ${layer.name}`">
                                <font-awesome-icon icon="ellipsis-h" />
                            </font-awesome-layers>
                        </div>
                    </div>

                    <ul v-if="menuId == j" class="layerMenu" v-click-outside="hideMenu">
                        <template v-if="isExternal">
                            <li @click="clickZoomLayer(layer)">Zoom a capa</li>
                            <li @click="clickDeleteExternal(layer)">Eliminar</li>
                        </template>
                        <template v-else>
                            <li @click="clickShowAttributes(layer)">Ver tabla de atributos</li>
                            <li @click="clickZoomLayer(layer)">Zoom a capa</li>
                            <li @click="clickManageSavedFilters(layer)">Administrar filtros guardados</li>
                        </template>
                    </ul>

                    <!-- También aplica a las capas del catálogo: una ráster avisa acá
                         mientras el servidor devuelve la imagen de la vista actual -->
                    <div v-if="layer.loading" class="ext-progress">
                        <div class="ext-progress-track">
                            <div
                                class="ext-progress-fill"
                                :class="{ 'ext-progress-fill--indeterminate': layer.progress == null }"
                                :style="layer.progress != null ? { width: (layer.progress * 100) + '%' } : {}"
                            ></div>
                        </div>
                        <span class="ext-progress-label">
                            {{ layer.progress != null ? Math.round(layer.progress * 100) + '%' : 'Cargando…' }}
                        </span>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Fuente y fechas de la capa en hover. Fixed porque .layer-item-content y
             .resizable (Layers.vue) recortan cualquier cosa absolute. -->
        <transition name="layer-info-fade">
            <div v-if="infoTip" :id="`layer-info-tip-${index}`" class="layer-info-tip"
                :class="{ 'layer-info-tip--below': infoTip.below }"
                :style="{ left: infoTip.x + 'px', top: infoTip.y + 'px' }" role="tooltip">
                <span class="layer-info-tip-fuente"><strong>Fuente:</strong> {{ infoTip.fuente }}</span>
                <span><strong>Fecha de actualización:</strong> {{ infoTip.fechaActualizacion }}</span>
                <span><strong>Fecha del dato:</strong> {{ infoTip.fechaDato }}</span>
            </div>
        </transition>

    </div>
</template>

<script>
import { formatter } from '@/components/mixin/formatter.js'
import vClickOutside from 'v-click-outside'
import { attributesTableService } from '@/services/layers/attributesTable.js'
import { LayerService } from '@/services/layers/layerService.js'
import { IMPORT_PROVIDER } from '@/services/layers/importedLayerRegistry.js'
import { isAttributeFilterEnabled, readSavedAttributeFilter } from '@/services/layers/attributeFilterState.js'


export default {
    props: {
        index: {
            type: Number,
            required: true
        },
        category: {
            type: Object,
            required: true
        },
        searchQuery: {
            type: String,
            default: ''
        },
        isSearchActive: {
            type: Boolean,
            default: false
        },
        checkState: {
            type: String, // 'all' | 'some' | 'none'
            default: 'none'
        },
        // Capas WMS externas de sesión: checkbox controla visibilidad (no activación de catálogo)
        // y el ícono de acción elimina la capa en vez de abrir el menú de atributos/zoom.
        isExternal: {
            type: Boolean,
            default: false
        }
    },

    mixins: [formatter],

    directives: {
        clickOutside: vClickOutside.directive
    },

    data() {
        return {
            showList: false,
            selected: false,
            menuId: -1,
            filterStateRevision: 0,
            activeOpacityLayer: null, // ID de la capa cuyo slider está activo
            infoTip: null // { fuente, fechaActualizacion, fechaDato, x, y, below, byFocus, layerId }
        }
    },

    mounted() {
        window.addEventListener('attribute-filter-state-change', this.refreshFilterStates);
        // Captura: el scroll de .resizable (Layers.vue) no burbujea hasta window
        window.addEventListener('scroll', this.onInfoTipScroll, { capture: true, passive: true });
    },

    beforeDestroy() {
        window.removeEventListener('attribute-filter-state-change', this.refreshFilterStates);
        window.removeEventListener('scroll', this.onInfoTipScroll, { capture: true });
        clearTimeout(this.infoTipTimer);
    },

    watch: {
        // Cubre toggleList, collapse y el watcher isSearchActive
        showList(visible) {
            if (!visible) this.hideInfoTip();
        },
        // Al filtrar, Layers.vue reutiliza esta instancia para otra categoría (:key="i")
        category() {
            this.hideInfoTip();
        },

        /**
         * Auto-expand when search results appear
         */
        'category.autoExpand': {
            immediate: true,
            handler(shouldExpand) {
                if (shouldExpand && this.isSearchActive) {
                    this.showList = true;
                }
            }
        },

        /**
         * Collapse when search is cleared
         */
        isSearchActive: {
            handler(isActive) {
                if (!isActive) {
                    this.showList = false;
                }
            }
        },

        /**
         * indeterminate is a DOM property, not an attribute; set it imperatively
         */
        checkState: {
            immediate: true,
            handler(val) {
                this.$nextTick(() => {
                    if (this.$refs.categoryCheckbox) {
                        this.$refs.categoryCheckbox.indeterminate = val === 'some';
                    }
                });
            }
        }
    },

    methods: {
        layerInfoDate(value) {
            if (!value || !this.moment.utc(value).isValid() || this.moment.utc(value).year() <= 1) return '—';
            return this.moment.utc(value).format('DD-MM-YYYY');
        },
        layerInfo(layer) {
            const dates = {
                fechaActualizacion: this.layerInfoDate(layer.fecha_actualizacion),
                fechaDato: this.layerInfoDate(layer.fecha_dato)
            };
            if (this.isExternal) {
                return { fuente: layer.provider.sourceLabel || layer.provider.url, ...dates };
            }
            if (layer.provider?.name === IMPORT_PROVIDER) {
                return {
                    fuente: 'Archivo importado (' + layer.fileName + ')',
                    ...dates
                };
            }
            return { fuente: (layer.fuente || '').trim() || 'Sin información', ...dates };
        },
        infoTipPosition(el) {
            const rect = el.getBoundingClientRect();
            const below = rect.top < 80;
            return {
                below,
                x: Math.min(rect.left, window.innerWidth - 288),
                y: below ? rect.bottom + 8 : rect.top - 8
            };
        },
        showInfoTip(layer, el, byFocus) {
            // El label puede haber desaparecido durante el delay (colapso, minimizar panel)
            if (!el || !el.isConnected || !el.offsetParent) return;
            this.infoTipAnchor = el;
            this.infoTip = { ...this.layerInfo(layer), ...this.infoTipPosition(el), byFocus, layerId: layer.id };
        },
        scheduleInfoTip(layer, el) {
            clearTimeout(this.infoTipTimer);
            this.infoTipTimer = setTimeout(() => this.showInfoTip(layer, el, false), 300);
        },
        hideInfoTip() {
            clearTimeout(this.infoTipTimer);
            this.infoTip = null;
        },
        // Un click de mouse también enfoca el checkbox: sin este filtro el tooltip
        // reaparece justo después de que mousedown lo cerró.
        onCheckboxFocus(layer, event) {
            if (event.target.matches(':focus-visible')) {
                this.showInfoTip(layer, event.target.nextElementSibling, true);
            }
        },
        // Con Tab el navegador hace scroll hasta la fila enfocada: ese tooltip se reubica, el de hover se cierra
        onInfoTipScroll() {
            if (this.infoTip && this.infoTip.byFocus) {
                Object.assign(this.infoTip, this.infoTipPosition(this.infoTipAnchor));
            } else {
                this.hideInfoTip();
            }
        },
        refreshFilterStates() {
            this.filterStateRevision += 1;
        },
        filterIsConfigured(layer) {
            this.filterStateRevision;
            return !!readSavedAttributeFilter(layer.id);
        },
        filterIsActive(layer) {
            this.filterStateRevision;
            return isAttributeFilterEnabled(layer.id);
        },
        filterState(layer) {
            if (!this.filterIsConfigured(layer)) return 'unconfigured';
            return this.filterIsActive(layer) ? 'active' : 'suspended';
        },
        filterIconTitle(layer) {
            if (!layer.selected) return `Activar capa ${layer.name}`;
            if (!this.filterIsConfigured(layer)) return `Sin filtro en ${layer.name}. Abrir tabla de atributos`;
            return this.filterIsActive(layer) ? `Desactivar filtro de ${layer.name}` : `Activar filtro de ${layer.name}`;
        },
        async toggleLayerFilter(layer) {
            this.$emit('toggleLayerFilter', this.category, layer);
        },
        symbologiesFor(layer) {
            return this.$store.getters['symbology/forLayer'](layer.id);
        },
        selectedSymbology(layer) {
            return this.$store.getters['symbology/selectedId'](layer.id) || '';
        },
        savedFiltersFor(layer) {
            return this.$store.getters['savedFilters/forLayer'](layer.id);
        },
        // filterStateRevision: mismo contador que usan filterIsConfigured/filterIsActive
        // (se incrementa en refreshFilterStates, disparado por el evento
        // attribute-filter-state-change) — el filtro guardado actualmente
        // cargado en la capa también depende de sessionStorage, así que
        // necesita el mismo recálculo forzado.
        selectedSavedFilterId(layer) {
            this.filterStateRevision;
            const match = this.savedFiltersFor(layer).find(preset => attributesTableService.matchesAppliedFilter(layer.id, preset));
            return match ? match.id : '';
        },
        // Reutiliza clickShowAttributes (misma activación/import de capa que
        // "Ver tabla de atributos") — sólo agrega la bandera que
        // AttributesTable.vue consume para abrir el modal de filtros
        // guardados de una vez.
        async clickManageSavedFilters(layer) {
            this.hideMenu();
            this.$store.dispatch('attributeTable/setPendingSavedFiltersModal', true);
            await this.clickShowAttributes(layer);
            const current = this.$store.getters['attributeTable/layer'];
            if (!current || current.id !== layer.id) {
                this.$store.dispatch('attributeTable/setPendingSavedFiltersModal', false);
            }
        },
        /**
         * Toggle list expansion
         */
        toggleList() {
            this.showList = !this.showList;
        },

        // Llamados imperativamente por el padre vía $refs (botones "Colapsar
        // todo"/"Abrir todo"): no hay binding reactivo para el estado de cada
        // categoría, así que se ordena una vez en vez de sincronizar un prop.
        expand() {
            this.showList = true;
        },
        collapse() {
            this.showList = false;
        },

        /**
         * Handle category checkbox click
         */
        clickCategoryCheckbox() {
            if (this.isExternal) {
                this.$emit("toggleCategoryVisibility", this.category);
            } else {
                this.$emit("toggleCategory", this.category);
            }
        },

        /**
         * Handle layer checkbox click
         */
        clickLayer(layerIndex, isChecked) {
            const layer = this.category.layers[layerIndex];

            if (this.isExternal) {
                this.$emit("toggleVisibility", layer);
                return;
            }

            this.$set(layer, 'selected', isChecked);
            if (isChecked) {
                this.$emit("selectLayer", this.category, layer);
            } else {
                this.$emit("removeLayer", this.category, layer);
            }
        },

        /**
         * Delete a session-only external WMS layer (not part of the persisted catalog)
         */
        clickDeleteExternal(layer) {
            this.$emit("deleteExternalLayer", layer);
        },

        /**
         * Toggle opacity slider visibility
         */
        toggleOpacitySlider(layerIndex) {
            // Si ya está abierto, cerrar; si no, abrir
            this.activeOpacityLayer = this.activeOpacityLayer === layerIndex ? null : layerIndex;
        },

        getLayerOpacity(layer) {
            const val = layer.opacity !== undefined ? layer.opacity : 1;
            return Math.round(val * 100);
        },

        /**
         * Update layer opacity
         */
        updateOpacity(event, layerIndex, layer) {
            const newOpacity = parseFloat(event.target.value);
            this.$set(layer, 'opacity', newOpacity);
            this.$emit("updateOpacity", {
                category: this.category,
                layer: layer,
                opacity: newOpacity
            });
        },

        /**
         * Toggle layer menu
         */
        setLayerMenu(id) {
            this.menuId = this.menuId === id ? -1 : id;
        },

        /**
         * Hide layer menu
         */
        hideMenu() {
            this.menuId = -1;
        },

        /**
         * Show attributes table for layer
         */
        async clickShowAttributes(layer) {
            console.log("clickShowAttributes", layer);
            this.hideMenu();
            try {
                let target = layer;
                if (layer.provider?.name === IMPORT_PROVIDER) {
                    target = { ...layer, selected: true };
                    // El descriptor va explícito: sin él loadLayerFromBackend lo recrea
                    // desde un fileObj sin manifest y registra la capa como
                    // upload:<fileId>, perdiendo el dataset de los archivos multicapa.
                    const entry = await LayerService.loadLayerFromBackend({
                        id: layer.fileId,
                        name: layer.fileName,
                        display_name: layer.name,
                    }, target);
                    if (!entry) return;
                    this.$store.dispatch('upsertImportedLayer', target);
                    this.$store.dispatch('addActiveLayer', target);
                } else if (!(this.$store.getters.getActiveLayers || []).some(l => l.id === layer.id)) {
                    // La capa se puede abrir desde el menú de atributos sin estar activa en
                    // el mapa: activarla acá evita destacados "fantasma" (fila clickeada sin
                    // que la capa esté realmente renderizada).
                    LayerService.addLayerToMap(layer, null, false);
                    this.$set(layer, 'selected', true);
                    this.$store.dispatch('addActiveLayer', layer);
                }
                await attributesTableService.openTable(target);
            } catch (error) {
                this.$store.dispatch('toast/show', { message: error.message || 'No se pudo abrir la tabla', type: 'error' });
            }
        },

        /**
         * Zoom to layer (activates it first if needed, handled by parent)
         */
        clickZoomLayer(layer) {
            this.hideMenu();
            this.$emit("zoomLayer", this.category, layer);
        },

        /**
         * Check if a layer matches the search query
         */
        isLayerMatch(layer) {
            if (!this.searchQuery) return false;
            return layer.name.toLowerCase().includes(this.searchQuery.toLowerCase());
        },

        /**
         * Highlight matched text in a string
         */
        highlightMatch(text, query) {
            if (!query || !text) return text;

            // Escape special regex characters
            const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`(${escapedQuery})`, 'gi');

            return text.replace(regex, '<span class="search-highlight">$1</span>');
        },
        clickSearchLayer(layer) {
            console.log("Clic en lupa para capa:", layer);
            this.$emit("searchLayer", this.category, layer);
        }

    }
}
</script>

<style scoped lang="scss">
p {
    margin: 0.5em 0;
}

.list-item-alt {
    color: #4D4D4D;
    font-size: 0.9em;
    padding: 0.25em 1em;
    margin-top: 0.5em;
    margin: 0.25em 0em;
}

.bottom-menu-fixed {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
}

.options-fixed {
    display: inherit;
    align-items: start;
    position: relative;
    cursor: pointer;
}

.item {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    background-color: #FCFCFC;
    padding: 10px;
    border-bottom: 1px solid #DBDBDB;
    color: #4D4D4D;
    font-size: 1em;
}

.training-title {
    margin: 0.5em;
}

.training-text {
    display: block;
    margin: 0.5em;
}

.list-item-info-alt {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--color-line);
}

.category-header-left {
    display: flex;
    align-items: center;
    gap: 6px;
    input[type="checkbox"]{
        accent-color: var(--color-orange);
    }
}

.items-list-collap {
    div {
        padding: 0.125em 0em;
    }
}

.btn-collapsible {
    border: none;
    background: transparent;
    color: var(--color-steel);
    cursor: pointer;
    transition: color 0.2s;

    // &:hover {
    //     color: #004B7A;
    // }

    // &:focus {
    //     outline: 2px solid #fe6565;
    //     outline-offset: 2px;
    // }
}

/* Search-related styles - Bold Contrast */
.search-match {
    color: var(--color-navy);
    font-weight: 700;
}

.category-name {
    color: var(--color-navy);
    font-weight: 700;
}

.layer-item {
    padding: 8px 4px;
    border-radius: 4px;
    transition: all 0.2s;
    overflow: visible;
    /* Permitir que el panel de opacidad salga */
    display: flex;
    flex-direction: column;
    position: relative;
    /* Para posicionar el menú contextual */

    &:hover {
        background: rgba(0, 111, 179, 0.05);
    }

    &.search-match-layer {
        background: var(--color-orange-light);
        border-left: 4px solid var(--color-orange);
        padding-left: 4px;
        box-shadow: inset 0 0 0 1px var(--color-orange);
    }
}

.layer-item-row {
    display: flex;
    /* Flex para alinear todo en una línea */
    align-items: center;
    justify-content: space-between;
    flex-wrap: nowrap;
}

.layer-item-content {
    display: flex;
    align-items: center;
    flex: 1;
    /* Ocupa el espacio disponible */
    overflow: hidden;
    /* Truncar texto si es necesario */
    margin-right: 8px;
    margin-left: 6px;
    color:var(--color-graphite);
    font-weight: 600;
    input[type="checkbox"]{
        accent-color:var(--color-orange);
    }
}

.layer-label {
    font-size: 0.9em;
    font-weight: normal;
    margin-left: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
}

.layer-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    /* No encoger iconos */
}

/* La fila de acciones es flex sin encoger: el select tiene que ir acotado */
.symbology-select {
    max-width: 90px;
    font-size: 0.72rem;
    padding: 1px 2px;
    border: 1px solid #d1d5db;
    border-radius: 3px;
    color: #666;
    background: #fff;
    cursor: pointer;
    text-overflow: ellipsis;
}

.action-icon {
    cursor: pointer;
    color: #666;

    &:hover {
        color: #006FB3;
    }
}

.filter-state-icon {
    position: relative;
    color: #6b7280;

    &.active {
        color: #16a34a;
    }

    &.unconfigured {
        color: #b6bdc7;
    }

    &.suspended {
        color: #596273;
    }
}

.filter-plus {
    color: #fff;
    font-size: .48em;
    transform: translate(42%, -42%);
    filter: drop-shadow(0 0 1px #6b7280);
}

.saved-filter-icon {
    color: #b6bdc7;

    &.active {
        color: #16a34a;
    }
}

/* Search highlight styling */
::v-deep .search-highlight {
    background: var(--color-orange);
    color: var(--color-white);
    padding: 2px 4px;
    border-radius: 3px;
    font-weight: 700;
    border: 1px solid var(--color-orange);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.layer-menu-icon {
    transition: transform 0.2s;

    &:hover {
        transform: scale(1.1);
    }
}

/* Expand/collapse transition */
.expand-enter-active {
    transition: all 0.3s ease;
    max-height: 1000px;
    overflow: hidden;
}

.expand-leave-active {
    transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
    max-height: 1000px;
    overflow: hidden;
}

.expand-enter,
.expand-leave-to {
    max-height: 0;
    opacity: 0;
}

/* Opacity Control */
.opacity-control {
    position: relative;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-left: auto;
    margin-right: 8px;
}

.opacity-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--color-orange);
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    border: 2px solid #fff;

    &:hover {
        background: var(--color-orange);
        transform: scale(1.15);
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.4);
    }

    &.active {
        background: var(--color-orange);
        box-shadow: 0 0 0 3px rgba(255, 165, 0, 0.3);
    }
}

.opacity-slider-panel {
    position: absolute;
    right: 25px;
    top: 50%;
    transform: translateY(-50%);
    background: var(--color-white);
    padding: 8px 12px;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 8px;
    z-index: 1000;
    min-width: 180px;
    border: 2px solid var(--color-orange);

    &::before {
        content: '';
        position: absolute;
        right: -8px;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-left: 8px solid var(--color-orange);
        border-top: 6px solid transparent;
        border-bottom: 6px solid transparent;
    }

    &::after {
        content: '';
        position: absolute;
        right: -6px;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-left: 6px solid var(--color-white);
        border-top: 5px solid transparent;
        border-bottom: 5px solid transparent;
    }
}

.opacity-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--color-orange);
    white-space: nowrap;
}

.opacity-slider {
    flex: 1;
    height: 4px;
    -webkit-appearance: none;
    appearance: none;
    background: linear-gradient(to right, #e0e0e0 0%, #4a90e2 100%);
    outline: none;
    border-radius: 2px;
    cursor: pointer;

    &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 16px;
        height: 16px;
        background: var(--color-orange);
        cursor: pointer;
        border-radius: 50%;
        border: 2px solid var(--color-white);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        transition: all 0.2s;

        &:hover {
            transform: scale(1.2);
            box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
        }
    }

    &::-moz-range-thumb {
        width: 16px;
        height: 16px;
        background: var(--color-orange);
        cursor: pointer;
        border-radius: 50%;
        border: 2px solid var(--color-white);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        transition: all 0.2s;

        &:hover {
            transform: scale(1.2);
            box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
        }
    }
}

.opacity-value {
    font-size: 11px;
    color: #333;
    font-weight: 600;
    min-width: 38px;
    text-align: right;
}

/* Progreso de carga de capas externas vectoriales (WFS/Esri REST) */
.ext-progress {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 2px 6px 4px 34px;
}

.ext-progress-track {
    flex: 1;
    height: 4px;
    border-radius: 2px;
    background: var(--color-line);
    overflow: hidden;
}

.ext-progress-fill {
    height: 100%;
    width: 0%;
    background: var(--color-orange);
    border-radius: 2px;
    transition: width 0.2s ease;

    &--indeterminate {
        width: 40% !important;
        animation: ext-progress-indeterminate 1.1s ease-in-out infinite;
    }
}

.ext-progress-label {
    font-size: 0.72em;
    color: var(--color-steel);
    white-space: nowrap;
}

@keyframes ext-progress-indeterminate {
    0% {
        margin-left: 0%;
    }
    50% {
        margin-left: 60%;
    }
    100% {
        margin-left: 0%;
    }
}

/* Tooltip de fuente y fechas: colores y tipografía de LayerLoadingNotice.vue */
.layer-info-tip {
    position: fixed;
    z-index: 1001; /* sobre .opacity-slider-panel */
    transform: translateY(-100%);

    display: flex;
    flex-direction: column;
    gap: 1px;
    max-width: 280px;
    padding: 10px 14px;

    background: var(--color-navy, #0e2a40);
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(14, 42, 64, 0.35);

    color: #fff;
    font-size: 0.78rem;
    line-height: 1.3;
    pointer-events: none;

    strong {
        font-size: 0.9rem;
    }

    /* Flecha hacia el nombre de la capa */
    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 16px;
        border: 6px solid transparent;
        border-top-color: var(--color-navy, #0e2a40);
    }

    &--below {
        transform: none;

        &::after {
            top: auto;
            bottom: 100%;
            border-top-color: transparent;
            border-bottom-color: var(--color-navy, #0e2a40);
        }
    }
}

.layer-info-tip-fuente {
    display: -webkit-box;
    /* autoprefixer: ignore next */
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
}

.layer-info-fade-enter-active,
.layer-info-fade-leave-active {
    transition: opacity 0.18s ease;
}

.layer-info-fade-enter,
.layer-info-fade-leave-to {
    opacity: 0;
}

@media (hover: none) {
    .layer-info-tip {
        display: none;
    }
}
</style>
